import React, { useState, useEffect, useContext } from 'react';
import { DataContext } from '../../init/getData';
import { 
  Row, 
  Col, 
  Card, 
  Button, 
  Alert, 
  Table, 
  Tag, 
  Spin, 
  Select, 
  Checkbox, 
  Tooltip, 
  Input, 
  Form,
  Modal,
  Progress,
  Tabs,
  Badge,
  Statistic,
  Radio
} from 'antd';
import { 
  ContainerOutlined,
  ShoppingOutlined,
  PlayCircleOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  ReloadOutlined,
  SearchOutlined,
  FileTextOutlined,
  CalculatorOutlined,
  DollarOutlined,
  BarChartOutlined,
  HistoryOutlined,
  CheckCircleFilled,
  CloseCircleFilled
} from '@ant-design/icons';
import toast from 'react-hot-toast';

const { Option } = Select;

function StockClosing() {
  const { userData, selectedCompany, api, warehouseData } = useContext(DataContext);
  const [allYears, setAllYears] = useState([]);
  const [openYears, setOpenYears] = useState([]);
  const [closedYears, setClosedYears] = useState([]);
  const [loading, setLoading] = useState(false);
  const [closing, setClosing] = useState(false);
  const [selectedYear, setSelectedYear] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [selectedWarehouses, setSelectedWarehouses] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [activeTab, setActiveTab] = useState('preview');
  const [showClosedYears, setShowClosedYears] = useState(false);
  const [newYearForm] = Form.useForm();

  // Load years
  useEffect(() => {
    if (selectedCompany?.id) {
      loadYears();
    }
  }, [selectedCompany]);

  const loadYears = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/year/getYears?companyId=${selectedCompany.id}`);
      if (response.data.status) {
        const years = response.data.data;
        setAllYears(years);
        setOpenYears(years.filter(year => !year.isClosed));
        setClosedYears(years.filter(year => year.isClosed));
      }
    } catch (error) {
      console.error('Error loading years:', error);
      toast.error('Failed to load years');
    } finally {
      setLoading(false);
    }
  };

  const handleYearSelect = async (year) => {
    setSelectedYear(year);
    setPreviewData(null);
    setSelectedWarehouses([]);
    newYearForm.resetFields();
    
    // Set default new year data
    const nextYear = parseInt(year.year) + 1;
    newYearForm.setFieldsValue({
      newYear: nextYear.toString(),
      startDate: `${nextYear}-01-01`,
      endDate: `${nextYear}-12-31`
    });
  };

  const loadStockPreview = async () => {
    if (!selectedYear) return;
    
    try {
      setLoading(true);
      const response = await api.get(`/year/getStockClosingPreview?companyId=${selectedCompany.id}&yearId=${selectedYear.id}`);
      if (response.data.status) {
        setPreviewData(response.data.data);
        setShowPreview(true);
        setActiveTab('preview');
        
        // Auto-select all warehouses
        if (response.data.data.warehouses) {
          setSelectedWarehouses(response.data.data.warehouses.map(w => w.warehouse.id));
        }
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Error loading stock preview:', error);
      toast.error(error.response?.data?.message || 'Failed to load stock data');
    } finally {
      setLoading(false);
    }
  };

  const handleWarehouseSelection = (warehouseIds) => {
    setSelectedWarehouses(warehouseIds);
  };

  const selectAllWarehouses = () => {
    if (previewData?.warehouses) {
      setSelectedWarehouses(previewData.warehouses.map(w => w.warehouse.id));
    }
  };

  const clearAllWarehouses = () => {
    setSelectedWarehouses([]);
  };

  const closeStock = async () => {
    if (!selectedYear || !previewData) return;

    if (selectedWarehouses.length === 0) {
      toast.error('Please select at least one warehouse');
      return;
    }

    const values = await newYearForm.validateFields();
    
    setClosing(true);
    try {
      const response = await api.post('/year/closeStockOnly', {
        yearId: selectedYear.id,
        newYearData: {
          year: values.newYear,
          startDate: values.startDate,
          endDate: values.endDate,
          companyId: selectedCompany.id
        },
        userId: userData.id,
        warehouseIds: selectedWarehouses
      });

      if (response.data.status) {
        toast.success('Stock closed successfully!');
        
        // Show success modal with details
        Modal.success({
          title: 'Stock Closing Completed',
          content: (
            <div>
              <p><strong>Summary:</strong></p>
              <ul>
                <li>Warehouses Processed: {response.data.data.summary.warehousesProcessed}</li>
                <li>Stock Items Transferred: {response.data.data.summary.stockItemsTransferred}</li>
                <li>Total Value: {response.data.data.stockResults.totalValueTransferred.toLocaleString()}</li>
                <li>Journal Vouchers Created: {response.data.data.summary.journalVouchersCreated}</li>
              </ul>
            </div>
          ),
          onOk: () => {
            // Reset form
            newYearForm.resetFields();
            setSelectedYear(null);
            setPreviewData(null);
            setSelectedWarehouses([]);
            setShowPreview(false);
            loadYears(); // Reload years
          }
        });
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Error closing stock:', error);
      toast.error(error.response?.data?.message || 'Failed to close stock');
    } finally {
      setClosing(false);
    }
  };

  // Year status indicator
  const YearStatusIndicator = ({ year }) => {
    if (year.isClosed) {
      return (
        <Tag color="red" icon={<CloseCircleFilled />}>
          Closed
        </Tag>
      );
    }
    return (
      <Tag color="green" icon={<CheckCircleFilled />}>
        Open
      </Tag>
    );
  };

  // Warehouse selection component
  const WarehouseSelector = () => {
    if (!previewData?.warehouses) return null;

    return (
      <Card title="Select Warehouses" size="small" style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <div>
              <strong>Select Warehouses to Transfer:</strong>
              <div style={{ fontSize: 12, color: '#666' }}>
                {selectedWarehouses.length} of {previewData.warehouses.length} selected
              </div>
            </div>
            <div>
              <Button size="small" onClick={selectAllWarehouses} style={{ marginRight: 8 }}>
                Select All
              </Button>
              <Button size="small" onClick={clearAllWarehouses}>
                Clear All
              </Button>
            </div>
          </div>
          
          <Select
            mode="multiple"
            style={{ width: '100%' }}
            placeholder="Select warehouses..."
            value={selectedWarehouses}
            onChange={handleWarehouseSelection}
            optionLabelProp="label"
            dropdownRender={menu => (
              <div>
                {menu}
                <div style={{ padding: '8px 12px', borderTop: '1px solid #f0f0f0' }}>
                  <Checkbox
                    checked={selectedWarehouses.length === previewData.warehouses.length}
                    onChange={(e) => {
                      if (e.target.checked) {
                        selectAllWarehouses();
                      } else {
                        clearAllWarehouses();
                      }
                    }}
                  >
                    Select All Warehouses
                  </Checkbox>
                </div>
              </div>
            )}
          >
            {previewData.warehouses.map(warehouse => (
              <Option 
                key={warehouse.warehouse.id} 
                value={warehouse.warehouse.id}
                label={warehouse.warehouse.name}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                  <div>
                    <strong>{warehouse.warehouse.name}</strong>
                    <div style={{ fontSize: 12, color: '#666' }}>
                      {warehouse.stockCount} items, {warehouse.uniqueProducts} unique products
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: 'bold' }}>
                      {warehouse.totalValue.toLocaleString()}
                    </div>
                    <Tag color="blue" style={{ fontSize: 11 }}>
                      Value
                    </Tag>
                  </div>
                </div>
              </Option>
            ))}
          </Select>
        </div>

        {selectedWarehouses.length > 0 && (
          <Alert
            message="Selected Warehouses"
            description={
              <div>
                {previewData.warehouses
                  .filter(w => selectedWarehouses.includes(w.warehouse.id))
                  .map(w => (
                    <Tag key={w.warehouse.id} color="blue" style={{ margin: '2px' }}>
                      {w.warehouse.name} ({w.stockCount} items)
                    </Tag>
                  ))}
              </div>
            }
            type="info"
            showIcon
          />
        )}
      </Card>
    );
  };

  // Statistics card
  const StatisticsCard = () => {
    if (!previewData?.totals) return null;

    const selectedWarehouseData = previewData.warehouses?.filter(w => 
      selectedWarehouses.includes(w.warehouse.id)
    ) || [];

    const selectedTotalValue = selectedWarehouseData.reduce((sum, w) => sum + w.totalValue, 0);
    const selectedTotalItems = selectedWarehouseData.reduce((sum, w) => sum + w.stockCount, 0);

    return (
      <Card title="Stock Summary" size="small" style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={6}>
            <Statistic
              title="Warehouses"
              value={selectedWarehouses.length}
              suffix={`/ ${previewData.totals.warehouseCount}`}
              prefix={<ShoppingOutlined style={{ color: '#1890ff' }} />}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Total Items"
              value={selectedTotalItems}
              suffix={`/ ${previewData.totals.totalItems}`}
              prefix={<ContainerOutlined style={{ color: '#52c41a' }} />}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Total Value"
              value={selectedTotalValue}
              precision={2}
              prefix={<DollarOutlined style={{ color: '#fa8c16' }} />}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Avg per Warehouse"
              value={selectedWarehouses.length > 0 ? selectedTotalValue / selectedWarehouses.length : 0}
              precision={2}
              prefix={<CalculatorOutlined style={{ color: '#722ed1' }} />}
            />
          </Col>
        </Row>
        
        <Progress
          percent={selectedWarehouses.length > 0 ? (selectedTotalItems / previewData.totals.totalItems * 100) : 0}
          status="active"
          style={{ marginTop: 16 }}
        />
      </Card>
    );
  };

  // Stock items table
  const StockItemsTable = () => {
    if (!previewData?.warehouses || selectedWarehouses.length === 0) return null;

    const selectedWarehouseData = previewData.warehouses.filter(w => 
      selectedWarehouses.includes(w.warehouse.id)
    );

    const allItems = [];
    selectedWarehouseData.forEach(warehouseData => {
      warehouseData.items.forEach(item => {
        allItems.push({
          key: `${warehouseData.warehouse.id}-${item.productCode}`,
          warehouse: warehouseData.warehouse.name,
          ...item
        });
      });
    });

    const columns = [
      {
        title: 'Warehouse',
        dataIndex: 'warehouse',
        key: 'warehouse',
        width: 150,
        render: (text) => <Tag color="blue">{text}</Tag>
      },
      {
        title: 'Product Code',
        dataIndex: 'productCode',
        key: 'productCode',
        width: 120
      },
      {
        title: 'Product Name',
        dataIndex: 'productName',
        key: 'productName',
        width: 200
      },
      {
        title: 'Quantity',
        dataIndex: 'quantity',
        key: 'quantity',
        width: 100,
        render: (qty) => <strong>{qty.toLocaleString()}</strong>
      },
      {
        title: 'Unit Cost',
        dataIndex: 'cost',
        key: 'cost',
        width: 100,
        render: (cost) => cost.toFixed(2)
      },
      {
        title: 'Total Value',
        dataIndex: 'totalValue',
        key: 'totalValue',
        width: 120,
        render: (value) => (
          <Tag color="green" style={{ fontWeight: 'bold' }}>
            {value.toFixed(2)}
          </Tag>
        )
      }
    ];

    return (
      <Card title="Stock Items to Transfer" size="small">
        <Table
          columns={columns}
          dataSource={allItems}
          size="small"
          pagination={{ pageSize: 10 }}
          scroll={{ y: 300 }}
          summary={pageData => {
            const totalQty = pageData.reduce((sum, item) => sum + item.quantity, 0);
            const totalValue = pageData.reduce((sum, item) => sum + item.totalValue, 0);

            return (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={3}>
                  <strong>Total</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3}>
                  <strong>{totalQty.toLocaleString()}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4}></Table.Summary.Cell>
                <Table.Summary.Cell index={5}>
                  <Tag color="green" style={{ fontWeight: 'bold' }}>
                    {totalValue.toFixed(2)}
                  </Tag>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            );
          }}
        />
      </Card>
    );
  };

  // Render year list
  const renderYearList = () => {
    const yearsToShow = showClosedYears ? [...openYears, ...closedYears] : openYears;
    
    return (
      <div style={{ maxHeight: 400, overflow: 'auto' }}>
        {yearsToShow.map(year => (
          <Card 
            key={year.id}
            size="small"
            style={{ 
              marginBottom: 8,
              border: selectedYear?.id === year.id ? '2px solid #1890ff' : '1px solid #f0f0f0',
              cursor: 'pointer',
              opacity: year.isClosed ? 0.8 : 1
            }}
            onClick={() => handleYearSelect(year)}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div>
                <strong style={{ fontSize: 16 }}>{year.year}</strong>
                <div style={{ fontSize: 12, color: '#666' }}>
                  {year.startDate} to {year.endDate}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <YearStatusIndicator year={year} />
                <div style={{ fontSize: 11, color: '#666' }}>
                  {year._count?.journalVouchers || 0} JVs
                </div>
              </div>
            </div>
            {year.isClosed && (
              <Alert
                message="Year is closed"
                description="You can still transfer stock from this closed year to a new year."
                type="info"
                showIcon
                style={{ marginTop: 8, fontSize: '11px' }}
              />
            )}
          </Card>
        ))}
      </div>
    );
  };

  return (
    <div style={{ padding: '20px' }}>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card 
            title="Stock Closing Management" 
            extra={
              <div style={{ display: 'flex', gap: 8 }}>
                <Radio.Group 
                  value={showClosedYears} 
                  onChange={(e) => setShowClosedYears(e.target.value)}
                  size="small"
                >
                  <Radio.Button value={false}>Open Years</Radio.Button>
                  <Radio.Button value={true}>All Years</Radio.Button>
                </Radio.Group>
                <Button 
                  type="primary" 
                  icon={<BarChartOutlined />}
                  onClick={loadYears}
                  loading={loading}
                >
                  Refresh Years
                </Button>
              </div>
            }
          >
            <Row gutter={16}>
              <Col span={8}>
                <Card 
                  title={`Select Year to Close Stock (${showClosedYears ? 'All' : 'Open'})`} 
                  size="small"
                  extra={
                    <Badge 
                      count={closedYears.length} 
                      style={{ backgroundColor: showClosedYears ? '#1890ff' : '#d9d9d9' }}
                    />
                  }
                >
                  {loading ? (
                    <div style={{ textAlign: 'center', padding: 20 }}>
                      <Spin />
                      <p>Loading years...</p>
                    </div>
                  ) : openYears.length === 0 && !showClosedYears ? (
                    <Alert
                      message="No Open Years Found"
                      description={
                        <div>
                          <p>All years are already closed.</p>
                          <Button 
                            type="link" 
                            onClick={() => setShowClosedYears(true)}
                            icon={<HistoryOutlined />}
                          >
                            Show Closed Years
                          </Button>
                        </div>
                      }
                      type="warning"
                      showIcon
                    />
                  ) : (
                    renderYearList()
                  )}
                </Card>
              </Col>

              <Col span={16}>
                {selectedYear ? (
                  <Card 
                    title={`Close Stock for Year: ${selectedYear.year} ${selectedYear.isClosed ? '(Closed Year)' : ''}`}
                    extra={
                      <div style={{ display: 'flex', gap: 8 }}>
                        <Button
                          type="primary"
                          icon={<EyeOutlined />}
                          onClick={loadStockPreview}
                          loading={loading}
                        >
                          Preview Stock
                        </Button>
                        <Button
                          type="text"
                          danger
                          onClick={() => {
                            setSelectedYear(null);
                            setPreviewData(null);
                            setShowPreview(false);
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    }
                  >
                    {selectedYear.isClosed && (
                      <Alert
                        message="Year Already Closed"
                        description="This year is already closed for financial transactions, but you can still transfer stock to a new year."
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                      />
                    )}

                    {showPreview ? (
                      <Tabs activeKey={activeTab} onChange={setActiveTab}>
                        <Tabs.TabPane tab="Preview & Selection" key="preview">
                          <WarehouseSelector />
                          <StatisticsCard />
                          
                          {selectedWarehouses.length > 0 && (
                            <Card title="New Year Configuration" size="small" style={{ marginBottom: 16 }}>
                              <Form
                                form={newYearForm}
                                layout="vertical"
                              >
                                <Row gutter={16}>
                                  <Col span={8}>
                                    <Form.Item
                                      label="New Year"
                                      name="newYear"
                                      rules={[{ required: true, message: 'Please enter new year' }]}
                                    >
                                      <input 
                                        type="number" 
                                        className="ant-input"
                                        placeholder="e.g., 2024"
                                        min={parseInt(selectedYear.year) + 1}
                                      />
                                    </Form.Item>
                                  </Col>
                                  <Col span={8}>
                                    <Form.Item
                                      label="Start Date"
                                      name="startDate"
                                      rules={[{ required: true, message: 'Please select start date' }]}
                                    >
                                      <input 
                                        type="date" 
                                        className="ant-input"
                                      />
                                    </Form.Item>
                                  </Col>
                                  <Col span={8}>
                                    <Form.Item
                                      label="End Date"
                                      name="endDate"
                                      rules={[{ required: true, message: 'Please select end date' }]}
                                    >
                                      <input 
                                        type="date" 
                                        className="ant-input"
                                      />
                                    </Form.Item>
                                  </Col>
                                </Row>
                              </Form>
                            </Card>
                          )}

                          <Alert
                            message="Stock Transfer Information"
                            description="This will create Opening Stock journal vouchers and transfer physical stock quantities to the new year."
                            type="info"
                            showIcon
                            style={{ marginBottom: 16 }}
                          />

                          <Button
                            type="primary"
                            size="large"
                            icon={<PlayCircleOutlined />}
                            onClick={closeStock}
                            loading={closing}
                            disabled={selectedWarehouses.length === 0}
                            style={{ width: '100%', marginTop: 16 }}
                          >
                            Transfer Stock from {selectedYear.year} to New Year
                          </Button>
                        </Tabs.TabPane>

                        <Tabs.TabPane tab="Stock Items Details" key="details">
                          <StockItemsTable />
                        </Tabs.TabPane>
                      </Tabs>
                    ) : (
                      <div style={{ textAlign: 'center', padding: 40 }}>
                        <ContainerOutlined style={{ fontSize: 48, color: '#1890ff', marginBottom: 16 }} />
                        <h3>Click "Preview Stock" to view available stock</h3>
                        <p>This will show you all stock items that can be transferred to the new year.</p>
                        {selectedYear.isClosed && (
                          <Alert
                            message="Note"
                            description="Even though this year is closed, you can still transfer its stock to a new year."
                            type="info"
                            showIcon
                            style={{ marginTop: 16 }}
                          />
                        )}
                      </div>
                    )}
                  </Card>
                ) : (
                  <div style={{ textAlign: 'center', padding: 60 }}>
                    <ShoppingOutlined style={{ fontSize: 64, color: '#d9d9d9', marginBottom: 24 }} />
                    <h3>Select a Year to Begin</h3>
                    <p>Choose a year from the list to close its stock to a new year</p>
                    <div style={{ marginTop: 16 }}>
                      <Radio.Group 
                        value={showClosedYears} 
                        onChange={(e) => setShowClosedYears(e.target.value)}
                      >
                        <Radio value={false}>Show Only Open Years</Radio>
                        <Radio value={true}>Show All Years (Including Closed)</Radio>
                      </Radio.Group>
                    </div>
                  </div>
                )}
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default StockClosing;